import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge } from "@chakra-ui/react";
import { FaClipboardCheck, FaSearch, FaFileContract, FaHandshake, FaChartLine } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const TEAL = "#0D7F6E";
const SAND = "#C2A878";
const OFF_WHITE = "#F0EBE0";

const PropertyServicesInvestmentStrategy: React.FC = () => {
  const steps = [
    { title: "Discovery", description: "Understand your retirement goals, tax position & borrowing capacity", icon: FaClipboardCheck },
    { title: "Strategy", description: "Design a tax-efficient portfolio plan aligned to your timeline", icon: FaSearch },
    { title: "Due Diligence", description: "Rigorous property analysis to ensure you're making the right choice", icon: FaFileContract },
    { title: "Acquisition", description: "Expert guidance through negotiation & settlement", icon: FaHandshake },
    { title: "Ongoing Support", description: "Regular reviews & accountability to keep you on track", icon: FaChartLine }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/services/investment-strategy"
        fallbackTitle="Investment Strategy Consulting – One Property Australia"
        fallbackDescription="Expert guidance on property investment strategies for high-income earners. Structured approach to building retirement wealth and minimising tax."
      />
      <NavbarWithCallToAction />

      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg={SAND} color={NAVY} px={4} py={2} borderRadius="full">For High-Income Earners</Badge>
            <Heading size="3xl" fontWeight="black">Strategic Property Investment</Heading>
            <Text fontSize="xl" maxW="3xl" lineHeight="1.8">
              A structured approach to building retirement wealth and minimising tax—designed for professionals who want clarity, long-term planning, and accountability
            </Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Our 5-Step Process</Heading>
          <Text fontSize="lg" color="gray.600">Clear, practical steps that give you confidence you're making the right moves</Text>
        </VStack>

        <Box maxW="4xl" mx="auto">
          {steps.map((step, index) => (
            <HStack key={index} spacing={6} mb={8} align="flex-start">
              <Box bg={index % 2 === 0 ? OFF_WHITE : "gray.50"} p={4} borderRadius="xl" flexShrink={0} border="2px solid" borderColor={index % 2 === 0 ? SAND : "gray.200"}>
                <Icon as={step.icon} boxSize={8} color={index % 2 === 0 ? TEAL : NAVY} />
              </Box>
              <VStack align="flex-start" spacing={2} flex={1}>
                <HStack>
                  <Badge bg={SAND} color={NAVY}>Step {index + 1}</Badge>
                  <Heading size="md" color="gray.800">{step.title}</Heading>
                </HStack>
                <Text color="gray.600">{step.description}</Text>
              </VStack>
            </HStack>
          ))}
        </Box>
      </Container>

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl">What You Get</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Retirement Gap Analysis</Heading>
              <Text fontSize="sm" color="gray.600">Understand exactly how property can bridge the gap between your super and desired lifestyle</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Tax Strategy Review</Heading>
              <Text fontSize="sm" color="gray.600">Identify how much tax you could save through strategic property structuring</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Personalised Roadmap</Heading>
              <Text fontSize="sm" color="gray.600">A clear, written plan with milestones tailored to your goals and timeline</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Property Selection Support</Heading>
              <Text fontSize="sm" color="gray.600">Research-backed property recommendations that align with your strategy</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Finance Structuring</Heading>
              <Text fontSize="sm" color="gray.600">Loan structures optimised for tax efficiency and wealth building</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Heading size="sm" mb={3} color={TEAL}>Ongoing Accountability</Heading>
              <Text fontSize="sm" color="gray.600">12 months of reviews and support to keep you on track toward your goals</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaClipboardCheck} boxSize={16} />
            <Heading size="xl">Stop Wondering, Start Planning</Heading>
            <Text fontSize="lg" maxW="2xl">Get the clarity and confidence you need to make informed decisions about your financial future. Book a complimentary strategy session.</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyServicesInvestmentStrategy;
